import React, { Component, ErrorInfo, ReactNode } from 'react';
import { trackError } from '@/utils/ErrorTracking';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  isAuthError?: boolean;
  isContentUpdateError?: boolean;
  isConnectionError?: boolean;
  isQuizSessionError?: boolean;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false
  };

  public static getDerivedStateFromError(error: Error): State {
    const errorMessage = error.message || '';
    
    const isAuthError = errorMessage.includes('auth') || 
                       errorMessage.includes('Authentication') ||
                       errorMessage.includes('token') ||
                       errorMessage.includes('sign in again');
    
    const isContentUpdateError = errorMessage.includes('Test content is being updated') ||
                                errorMessage.includes('No questions available');
    
    const isConnectionError = errorMessage.includes('Connection issue') ||
                             errorMessage.includes('temporarily unavailable') ||
                             errorMessage.includes('timed out');
    
    const isQuizSessionError = errorMessage.includes('Quiz session not found') ||
                              errorMessage.includes('session may have expired');
    
    return { 
      hasError: true, 
      error,
      isAuthError,
      isContentUpdateError,
      isConnectionError,
      isQuizSessionError
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const metadata = {
      component_stack: errorInfo.componentStack ?? ''
    };

    trackError(error, {
      type: 'js',
      component: 'ErrorBoundary',
      metadata
    });
  }

  public render() {
    if (this.state.hasError) {
      const { error, isAuthError, isContentUpdateError, isConnectionError, isQuizSessionError } = this.state;
      
      // Determine error type and messaging
      let errorIcon = '❌';
      let errorTitle = 'Something Went Wrong';
      let errorMessage = 'An unexpected error occurred. Please try refreshing the page.';
      const showRetryButton = true;
      
      if (isContentUpdateError) {
        errorIcon = '🔄';
        errorTitle = 'Content Update in Progress';
        errorMessage = 'Test content is being updated with new questions. This usually takes just a few minutes.';
      } else if (isAuthError) {
        errorIcon = '🔐';
        errorTitle = 'Authentication Error';
        errorMessage = 'Please sign in again to continue. This often happens when switching between environments.';
      } else if (isConnectionError) {
        errorIcon = '🌐';
        errorTitle = 'Connection Issue';
        errorMessage = 'Having trouble connecting to our servers. Please check your internet connection and try again.';
      } else if (isQuizSessionError) {
        errorIcon = '📝';
        errorTitle = 'Quiz Session Issue';
        errorMessage = 'Your quiz session has expired or was not found. You can start a new quiz from the dashboard.';
      }
      
      return (
        <div className="flex items-center justify-center min-h-screen bg-neutral-50">
          <div className="text-center max-w-md p-8 bg-white rounded-lg shadow-ledge-lg">
            <h1 className="text-2xl font-bold text-error-600 mb-4">
              {errorIcon} {errorTitle}
            </h1>
            <p className="text-neutral-700 mb-6">
              {errorMessage}
            </p>
            <div className="space-y-3">
              {showRetryButton && (
                <button
                  onClick={() => window.location.reload()}
                  className="w-full px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700"
                >
                  {isContentUpdateError ? 'Check Again' :
                   isConnectionError ? 'Retry Connection' :
                   isQuizSessionError ? 'Go to Dashboard' : 'Refresh Page'}
                </button>
              )}
              
              {(isAuthError || isQuizSessionError) && (
                <button
                  onClick={() => {
                    // Clear auth-related data and redirect to appropriate page
                    if (isAuthError) {
                      localStorage.clear();
                      window.location.href = '/signin';
                    } else {
                      window.location.href = '/dashboard';
                    }
                  }}
                  className="w-full px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700"
                >
                  {isAuthError ? 'Sign In Again' : 'Start New Quiz'}
                </button>
              )}
              
              <button
                onClick={() => {
                  // Clear local storage and redirect to home
                  localStorage.clear();
                  window.location.href = '/';
                }}
                className="w-full px-4 py-2 bg-neutral-200 text-neutral-700 rounded hover:bg-neutral-300"
              >
                Return Home
              </button>
            </div>
            {/* Only show detailed error information in development */}
            {import.meta.env.DEV && error && (
              <details className="mt-4 text-xs text-left">
                <summary className="cursor-pointer text-neutral-500">Error Details (Dev Only)</summary>
                <pre className="mt-2 p-2 bg-neutral-100 rounded text-neutral-600 overflow-auto">
                  {error.message}
                  {error.stack && `\n\nStack trace:\n${error.stack}`}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Unused component - commenting out
// class QuizErrorBoundary extends React.Component<Props, State> {
//   public state: State = {
//     hasError: false
//   };
// 
//   public static getDerivedStateFromError(): State {
//     return { hasError: true };
//   }
// 
//   componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
//     prodLogger.prodDebug('QuizError', 'Quiz component error', {
//       error,
//       componentStack: errorInfo.componentStack
//     });
//   }
// 
//   render() {
//     if (this.state.hasError) {
//       return <div className="text-red-600">Something went wrong with the quiz. Please try again.</div>;
//     }
// 
//     return this.props.children;
//   }
// }

export default ErrorBoundary;